import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, Search, Edit, Eye, ChevronLeft, Save, MapPin, Calendar, DollarSign, CheckCircle, Clock, AlertCircle, FileText, Receipt } from 'lucide-react';
import { supabase } from './supabaseClient';
import { AdminJobOrderView, WorkerJobOrderView } from './JobOrderView';

// Job Form Component
const JobForm = ({ 
  formData, 
  onFormDataChange, 
  onSave, 
  onCancel, 
  saving, 
  isEditing,
  customers,
  estimates 
}) => {
  const [localFormData, setLocalFormData] = useState(formData);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [availableEstimates, setAvailableEstimates] = useState([]);

  useEffect(() => {
    setLocalFormData(formData);
    if (formData.customer_id) {
      const customer = customers.find(c => c.id === formData.customer_id);
      setSelectedCustomer(customer);
      
      // Filter estimates for this customer
      const customerEstimates = estimates.filter(e => 
        e.customer_id === formData.customer_id && e.status === 'Accepted'
      );
      setAvailableEstimates(customerEstimates);
    }
    if (formData.estimate_id) {
      const estimate = estimates.find(e => e.id === formData.estimate_id);
      setSelectedEstimate(estimate);
    }
  }, [formData, customers, estimates]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...localFormData, [name]: value };
    setLocalFormData(newData);
    onFormDataChange(newData);
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    setSelectedCustomer(customer);
    
    // Load estimates for this customer
    const customerEstimates = estimates.filter(e => 
      e.customer_id === parseInt(customerId) && e.status === 'Accepted'
    );
    setAvailableEstimates(customerEstimates);
    
    const newData = {
      ...localFormData,
      customer_id: parseInt(customerId),
      estimate_id: null,
      job_address: customer?.address || '',
      job_city: customer?.city || 'Thunder Bay',
      job_province: customer?.province || 'ON',
      job_postal_code: customer?.postal_code || ''
    };
    setLocalFormData(newData);
    onFormDataChange(newData);
  };

  const handleEstimateSelect = async (estimateId) => {
    if (!estimateId) {
      setSelectedEstimate(null);
      return;
    }

    const estimate = estimates.find(e => e.id === parseInt(estimateId));
    setSelectedEstimate(estimate);

    // Load estimate items
    try {
      const { data: items, error } = await supabase
        .from('estimate_items')
        .select('*')
        .eq('estimate_id', estimateId)
        .order('sort_order');

      if (error) throw error;

      const newData = {
        ...localFormData,
        estimate_id: parseInt(estimateId),
        job_address: estimate.job_address || localFormData.job_address,
        job_city: estimate.job_city || localFormData.job_city,
        job_province: estimate.job_province || localFormData.job_province,
        job_postal_code: estimate.job_postal_code || localFormData.job_postal_code,
        total_amount: estimate.total_amount,
        estimate_items: items || []
      };
      setLocalFormData(newData);
      onFormDataChange(newData);
    } catch (error) {
      console.error('Error loading estimate items:', error);
    }
  };

  const handleSave = () => {
    onSave(localFormData);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className={`${isEditing ? 'bg-green-500' : 'bg-pink-500'} text-white p-6 rounded-t-xl`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Briefcase size={32} />
            <h2 className="text-2xl font-bold">
              {isEditing ? 'Edit Job' : 'New Job'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Job Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Number
            </label>
            <input
              type="text"
              name="job_number"
              value={localFormData.job_number || ''}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              placeholder="Auto-generated"
              readOnly={!isEditing}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Date *
            </label>
            <input
              type="date"
              name="start_date"
              value={localFormData.start_date || new Date().toISOString().split('T')[0]}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={localFormData.status || 'Scheduled'}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="Scheduled">Scheduled</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="On Hold">On Hold</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Customer Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Customer *
          </label>
          <select
            value={localFormData.customer_id || ''}
            onChange={(e) => handleCustomerSelect(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          >
            <option value="">Select a customer</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.first_name} {customer.last_name} {customer.company ? `(${customer.company})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Customer Info Display */}
        {selectedCustomer && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-800 mb-2">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Name:</span> {selectedCustomer.first_name} {selectedCustomer.last_name}
              </div>
              <div>
                <span className="text-gray-600">Phone:</span> {selectedCustomer.phone}
              </div>
              <div>
                <span className="text-gray-600">Email:</span> {selectedCustomer.email || 'N/A'}
              </div>
              <div>
                <span className="text-gray-600">Address:</span> {selectedCustomer.address}, {selectedCustomer.city}
              </div>
            </div>
          </div>
        )}

        {/* Estimate Selection (optional) */}
        {localFormData.customer_id && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link to Estimate (Optional)
            </label>
            <select
              value={localFormData.estimate_id || ''}
              onChange={(e) => handleEstimateSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="">No linked estimate</option>
              {availableEstimates.map(estimate => (
                <option key={estimate.id} value={estimate.id}>
                  {estimate.estimate_number} - ${estimate.total_amount} ({new Date(estimate.estimate_date).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Estimate Info Display */}
        {selectedEstimate && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-800 mb-2">Linked Estimate</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Estimate #:</span> {selectedEstimate.estimate_number}
              </div>
              <div>
                <span className="text-gray-600">Date:</span> {new Date(selectedEstimate.estimate_date).toLocaleDateString()}
              </div>
              <div>
                <span className="text-gray-600">Amount:</span> ${selectedEstimate.total_amount}
              </div>
            </div>
          </div>
        )}

        {/* Job Location */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Location</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Address *
              </label>
              <input
                type="text"
                name="job_address"
                value={localFormData.job_address || ''}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="Job site address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                name="job_city"
                value={localFormData.job_city || 'Thunder Bay'}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal Code
              </label>
              <input
                type="text"
                name="job_postal_code"
                value={localFormData.job_postal_code || ''}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="P7A 1B2"
              />
            </div>
          </div>
        </div>

        {/* Completion Date */}
        {localFormData.status === 'Completed' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Completion Date *
            </label>
            <input
              type="date"
              name="completion_date"
              value={localFormData.completion_date || ''}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              This date is used for accounts receivable aging calculations
            </p>
          </div>
        )}

        {/* Total Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              name="total_amount"
              value={localFormData.total_amount || ''}
              onChange={handleFormChange}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            name="notes"
            value={localFormData.notes || ''}
            onChange={handleFormChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            placeholder="Job notes, special instructions, crew assignments..."
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
              saving
                ? 'bg-gray-400 cursor-not-allowed'
                : isEditing
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-pink-500 hover:bg-pink-600'
            } text-white`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>{isEditing ? 'Update Job' : 'Save Job'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Jobs Management Component
function JobsManagement({ conversionData, onConversionComplete }) {
  const [activeView, setActiveView] = useState('list');
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showJobOrderView, setShowJobOrderView] = useState(null); // 'admin' or 'worker'
  const [jobOrderData, setJobOrderData] = useState({ job: null, customer: null, items: [] });
  const [formData, setFormData] = useState({
    job_number: '',
    customer_id: null,
    estimate_id: null,
    job_address: '',
    job_city: 'Thunder Bay',
    job_province: 'ON',
    job_postal_code: '',
    start_date: new Date().toISOString().split('T')[0],
    completion_date: null,
    status: 'Scheduled',
    total_amount: 0,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Generate job number helper
  const generateJobNumber = () => {
    const year = new Date().getFullYear();
    const existingNumbers = jobs
      .filter(j => j.job_number?.startsWith(`JOB-${year}-`))
      .map(j => {
        const parts = j.job_number.split('-');
        return parts.length === 3 ? parseInt(parts[2]) : 0;
      })
      .filter(n => !isNaN(n));

    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    return `JOB-${year}-${nextNumber.toString().padStart(3, '0')}`;
  };

  useEffect(() => {
    // Handle estimate conversion
    if (conversionData && jobs.length > 0) {
      const year = new Date().getFullYear();
      const existingNumbers = jobs
        .filter(j => j.job_number?.startsWith(`JOB-${year}-`))
        .map(j => {
          const parts = j.job_number.split('-');
          return parts.length === 3 ? parseInt(parts[2]) : 0;
        })
        .filter(n => !isNaN(n));

      const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
      const jobNumber = `JOB-${year}-${nextNumber.toString().padStart(3, '0')}`;

      const newFormData = {
        job_number: jobNumber,
        customer_id: conversionData.customer_id,
        estimate_id: conversionData.estimate_id,
        job_address: conversionData.job_address || '',
        job_city: conversionData.job_city || 'Thunder Bay',
        job_province: conversionData.job_province || 'ON',
        job_postal_code: conversionData.job_postal_code || '',
        start_date: new Date().toISOString().split('T')[0],
        completion_date: null,
        status: 'Scheduled',
        total_amount: conversionData.total_amount || 0,
        notes: conversionData.notes || '',
        estimate_items: conversionData.estimate_items || []
      };
      setFormData(newFormData);
      setActiveView('new');
      if (onConversionComplete) {
        onConversionComplete();
      }
    }
  }, [conversionData, jobs, onConversionComplete]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load jobs with customer data
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select(`
          *,
          customer:customers (
            id,
            first_name,
            last_name,
            company,
            phone
          ),
          estimate:estimates (
            estimate_number,
            total_amount
          )
        `)
        .order('created_at', { ascending: false });

      if (jobsError) throw jobsError;

      // Load customers
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('*')
        .eq('status', 'Active')
        .order('first_name');

      if (customersError) throw customersError;

      // Load accepted estimates
      const { data: estimatesData, error: estimatesError } = await supabase
        .from('estimates')
        .select('*')
        .eq('status', 'Accepted')
        .order('estimate_date', { ascending: false });

      if (estimatesError) throw estimatesError;

      setJobs(jobsData || []);
      setCustomers(customersData || []);
      setEstimates(estimatesData || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      job_number: '',
      customer_id: null,
      estimate_id: null,
      job_address: '',
      job_city: 'Thunder Bay',
      job_province: 'ON',
      job_postal_code: '',
      start_date: new Date().toISOString().split('T')[0],
      completion_date: null,
      status: 'Scheduled',
      total_amount: 0,
      notes: ''
    });
  };

  const handleFormDataChange = (newData) => {
    setFormData(newData);
  };

  const handleSave = async (dataToSave) => {
    if (!dataToSave.customer_id) {
      alert('Please select a customer');
      return;
    }

    if (!dataToSave.job_address) {
      alert('Please enter a job address');
      return;
    }

    try {
      setSaving(true);

      if (activeView === 'edit' && selectedJob) {
        // Update existing job
        const { estimate_items, customer, estimate, ...jobData } = dataToSave;
        
        const { error } = await supabase
          .from('jobs')
          .update(jobData)
          .eq('id', selectedJob.id);

        if (error) throw error;

        alert('Job updated successfully!');
      } else {
        // Create new job
        const { estimate_items, customer, estimate, ...jobData } = dataToSave;

        // Generate job number if not provided
        if (!jobData.job_number) {
          const year = new Date().getFullYear();
          const { data: existingJobs, error: fetchError } = await supabase
            .from('jobs')
            .select('job_number')
            .like('job_number', `JOB-${year}-%`);
          
          if (fetchError) throw fetchError;
          
          const existingNumbers = (existingJobs || [])
            .map(j => {
              const parts = j.job_number.split('-');
              return parts.length === 3 ? parseInt(parts[2]) : 0;
            })
            .filter(n => !isNaN(n));
          
          const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
          jobData.job_number = `JOB-${year}-${nextNumber.toString().padStart(3, '0')}`;
        }

        const { error } = await supabase
          .from('jobs')
          .insert([jobData]);

        if (error) throw error;

        alert('Job created successfully!');
      }

      await loadData();
      setActiveView('list');
      resetForm();

    } catch (error) {
      console.error('Error saving job:', error);
      alert('Error saving job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setActiveView('list');
    resetForm();
  };

  const showJobOrder = async (job, viewType) => {
    try {
      // Load customer details
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', job.customer_id)
        .single();

      if (customerError) throw customerError;

      // Load estimate items if job has an estimate
      let itemsData = [];
      if (job.estimate_id) {
        const { data: items, error: itemsError } = await supabase
          .from('estimate_items')
          .select('*')
          .eq('estimate_id', job.estimate_id)
          .order('sort_order');

        if (itemsError) throw itemsError;
        itemsData = items || [];
      }

      setJobOrderData({
        job: job,
        customer: customerData,
        items: itemsData
      });
      setShowJobOrderView(viewType);
    } catch (error) {
      console.error('Error loading job order data:', error);
      alert('Error loading job order data: ' + error.message);
    }
  };

  const generateInvoice = async (job) => {
    // Check if job is completed
    if (job.status !== 'Completed') {
      alert('Only completed jobs can be invoiced. Please mark this job as completed first.');
      return;
    }

    // Check if invoice already exists for this job
    const { data: existingInvoice } = await supabase
      .from('invoices')
      .select('invoice_number')
      .eq('job_id', job.id)
      .single();

    if (existingInvoice) {
      const proceed = window.confirm(
        `An invoice already exists for this job: ${existingInvoice.invoice_number}\n\nDo you want to view existing invoices instead?`
      );
      if (proceed) {
        // Navigate to invoicing page - this will be handled by the parent component
        alert('Please use the Invoicing module from the main navigation to view existing invoices.');
      }
      return;
    }

    try {
      // Generate invoice number
      const year = new Date().getFullYear();
      const { data: existingInvoices, error: invoiceError } = await supabase
        .from('invoices')
        .select('invoice_number')
        .like('invoice_number', `INV-${year}-%`);

      if (invoiceError) throw invoiceError;

      const existingNumbers = (existingInvoices || [])
        .map(inv => {
          const parts = inv.invoice_number.split('-');
          return parts.length === 3 ? parseInt(parts[2]) : 0;
        })
        .filter(n => !isNaN(n));

      const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
      const invoiceNumber = `INV-${year}-${nextNumber.toString().padStart(3, '0')}`;

      // Calculate due date (30 days from today)
      const invoiceDate = new Date().toISOString().split('T')[0];
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30);
      const dueDateStr = dueDate.toISOString().split('T')[0];

      // Create invoice
      const { data: newInvoice, error: createError } = await supabase
        .from('invoices')
        .insert([{
          invoice_number: invoiceNumber,
          job_id: job.id,
          customer_id: job.customer_id,
          invoice_date: invoiceDate,
          due_date: dueDateStr,
          total_amount: job.total_amount,
          status: 'Draft',
          notes: `Invoice for Job: ${job.job_number}\nLocation: ${job.job_address}, ${job.job_city}, ${job.job_province}`
        }])
        .select()
        .single();

      if (createError) throw createError;

      // If job has an estimate, copy estimate items to invoice items
      if (job.estimate_id) {
        const { data: estimateItems, error: itemsError } = await supabase
          .from('estimate_items')
          .select('*')
          .eq('estimate_id', job.estimate_id)
          .order('sort_order');

        if (!itemsError && estimateItems && estimateItems.length > 0) {
          const invoiceItems = estimateItems.map((item, index) => ({
            invoice_id: newInvoice.id,
            description: item.description,
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            amount: parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 0),
            sort_order: index
          }));

          const { error: insertItemsError } = await supabase
            .from('invoice_items')
            .insert(invoiceItems);

          if (insertItemsError) {
            console.error('Error creating invoice items:', insertItemsError);
            // Don't fail the whole operation, just log the error
          }
        }
      }

      alert(`Invoice ${invoiceNumber} created successfully!\n\nThe invoice is in Draft status. Go to the Invoicing module to view, print, and manage invoices.`);
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Failed to generate invoice: ' + error.message);
    }
  };

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    const customer = job.customer;
    const customerName = customer ? `${customer.first_name} ${customer.last_name} ${customer.company || ''}` : '';
    
    const matchesSearch = 
      job.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.job_address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || job.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Show form for new or edit
  if (activeView === 'new' || activeView === 'edit') {
    return (
      <JobForm
        formData={formData}
        onFormDataChange={handleFormDataChange}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        isEditing={activeView === 'edit'}
        customers={customers}
        estimates={estimates}
      />
    );
  }

  // Job Detail View
  if (activeView === 'detail') {
    return (
      <>
        {/* Job Order Modals */}
        {showJobOrderView === 'admin' && (
          <AdminJobOrderView
            job={jobOrderData.job}
            customer={jobOrderData.customer}
            estimateItems={jobOrderData.items}
            onBack={() => setShowJobOrderView(null)}
            onPrint={() => {}}
            onSwitchToWorkerView={() => setShowJobOrderView('worker')}
          />
        )}
        {showJobOrderView === 'worker' && (
          <WorkerJobOrderView
            job={jobOrderData.job}
            customer={jobOrderData.customer}
            estimateItems={jobOrderData.items}
            onBack={() => setShowJobOrderView(null)}
            onPrint={() => {}}
            onSwitchToAdminView={() => setShowJobOrderView('admin')}
          />
        )}

        <div className="bg-white rounded-xl shadow-lg">
          {/* Header */}
          <div className="bg-pink-500 text-white p-6 rounded-t-xl">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <Briefcase size={32} />
                <div>
                  <h2 className="text-2xl font-bold">{selectedJob?.job_number}</h2>
                  <p className="text-pink-100">
                    {selectedJob?.customer ?
                      `${selectedJob.customer.first_name} ${selectedJob.customer.last_name}` :
                      'No customer'
                    }
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => generateInvoice(selectedJob)}
                  disabled={selectedJob?.status !== 'Completed'}
                  className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                    selectedJob?.status === 'Completed'
                      ? 'bg-orange-500 hover:bg-orange-600 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title={selectedJob?.status === 'Completed' ? 'Generate Invoice' : 'Only completed jobs can be invoiced'}
                >
                  <Receipt size={20} />
                  <span>Invoice</span>
                </button>
                <button
                  onClick={() => showJobOrder(selectedJob, 'admin')}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  title="Admin Job Order (With Pricing)"
                >
                  <FileText size={20} />
                  <span>Admin Order</span>
                </button>
                <button
                  onClick={() => showJobOrder(selectedJob, 'worker')}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  title="Worker Job Order (No Pricing)"
                >
                  <FileText size={20} />
                  <span>Worker Order</span>
                </button>
                <button
                  onClick={() => {
                    setFormData({
                      ...selectedJob,
                      start_date: selectedJob.start_date || new Date().toISOString().split('T')[0],
                      completion_date: selectedJob.completion_date || null
                    });
                    setActiveView('edit');
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Edit size={20} />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => setActiveView('list')}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <ChevronLeft size={20} />
                  <span>Back</span>
                </button>
              </div>
            </div>
          </div>

        {/* Job Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Job Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Job Number:</span>
                  <span className="font-medium">{selectedJob?.job_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Start Date:</span>
                  <span className="font-medium">
                    {selectedJob?.start_date ? new Date(selectedJob.start_date).toLocaleDateString() : 'Not set'}
                  </span>
                </div>
                {selectedJob?.completion_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completion Date:</span>
                    <span className="font-medium">{new Date(selectedJob.completion_date).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${
                    selectedJob?.status === 'Completed' ? 'text-green-600' :
                    selectedJob?.status === 'In Progress' ? 'text-blue-600' :
                    selectedJob?.status === 'On Hold' ? 'text-yellow-600' :
                    selectedJob?.status === 'Cancelled' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {selectedJob?.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="font-bold text-lg">${selectedJob?.total_amount}</span>
                </div>
                {selectedJob?.estimate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Linked Estimate:</span>
                    <span className="font-medium">{selectedJob.estimate.estimate_number}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Job Location */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Location</h3>
              <div className="space-y-2">
                <p>{selectedJob?.job_address}</p>
                <p>{selectedJob?.job_city}, {selectedJob?.job_province} {selectedJob?.job_postal_code}</p>
              </div>
            </div>
          </div>

          {selectedJob?.notes && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Notes</h3>
              <p className="text-gray-600 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{selectedJob.notes}</p>
            </div>
          )}
        </div>
      </div>
      </>
    );
  }


  // Get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'Completed':
        return <CheckCircle className="text-green-600" size={20} />;
      case 'In Progress':
        return <Clock className="text-blue-600" size={20} />;
      case 'On Hold':
        return <AlertCircle className="text-yellow-600" size={20} />;
      case 'Cancelled':
        return <AlertCircle className="text-red-600" size={20} />;
      default:
        return <Calendar className="text-gray-600" size={20} />;
    }
  };

  // Jobs List View (Default)
  return (
    <div className="bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="bg-pink-500 text-white p-6 rounded-t-xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Briefcase size={32} />
            <div>
              <h2 className="text-2xl font-bold">Jobs Management</h2>
              <p className="text-pink-100">{jobs.length} jobs</p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setFormData(prev => ({
                ...prev,
                job_number: generateJobNumber()
              }));
              setActiveView('new');
            }}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus size={20} />
            <span>New Job</span>
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="p-6 border-b">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by job number, customer, or address..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="">All Statuses</option>
              <option value="Scheduled">Scheduled</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="On Hold">On Hold</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading jobs...</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredJobs.map(job => (
              <div key={job.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between gap-4">
                  <div className="bg-pink-100 p-2 rounded-full flex-shrink-0">
                    <Briefcase className="text-pink-600" size={24} />
                  </div>

                  <div className="flex-shrink-0 w-36">
                    <h3 className="text-base font-semibold text-gray-800">{job.job_number}</h3>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-base text-gray-600 truncate">
                      {job.customer ?
                        `${job.customer.first_name} ${job.customer.last_name}${job.customer.company ? ` (${job.customer.company})` : ''}` :
                        'No customer'
                      }
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 text-base text-gray-600 flex-shrink-0">
                    <DollarSign size={18} />
                    <span>${job.total_amount}</span>
                  </div>

                  <div className="flex items-center space-x-2 text-base text-gray-600 flex-shrink-0 w-28">
                    <Calendar size={18} />
                    <span className="truncate">{job.start_date ? new Date(job.start_date).toLocaleDateString() : 'Not set'}</span>
                  </div>

                  <div className="flex items-center space-x-2 text-base text-gray-600 flex-shrink-0 w-32">
                    <MapPin size={18} />
                    <span className="truncate">{job.job_city}</span>
                  </div>

                  <div className="flex items-center space-x-1 flex-shrink-0">
                    {getStatusIcon(job.status)}
                    <span className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                      job.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      job.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      job.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
                      job.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {job.status}
                    </span>
                  </div>

                  <div className="flex space-x-1 flex-shrink-0">
                    <button
                      onClick={() => showJobOrder(job, 'admin')}
                      className="bg-purple-500 hover:bg-purple-600 text-white px-2 py-2 rounded-lg transition-colors text-xs"
                      title="Admin Job Order"
                    >
                      Admin
                    </button>
                    <button
                      onClick={() => showJobOrder(job, 'worker')}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-2 rounded-lg transition-colors text-xs"
                      title="Worker Job Order"
                    >
                      Worker
                    </button>
                    <button
                      onClick={() => {
                        setSelectedJob(job);
                        setActiveView('detail');
                      }}
                      className="bg-pink-500 hover:bg-pink-600 text-white p-2 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedJob(job);
                        setFormData({
                          ...job,
                          start_date: job.start_date || new Date().toISOString().split('T')[0],
                          completion_date: job.completion_date || null
                        });
                        setActiveView('edit');
                      }}
                      className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-colors"
                      title="Edit Job"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredJobs.length === 0 && (
          <div className="text-center py-8">
            <Briefcase className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm || statusFilter ? 'No jobs found matching your criteria.' : 'No jobs yet. Create your first job!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default JobsManagement;